import { call, put, select, takeEvery } from "@redux-saga/core/effects";
import { createAction, EntityId, PayloadAction } from "@reduxjs/toolkit";
import {
  AuthorData,
  AuthorInfo,
  authorProfileActions,
  authorProfileAPI,
  FeaturedLink,
  selectIsAdminAuthenticated,
  Tool,
  ToolCategory,
  ToolCategoryRelation,
} from "../features";
import { pushNotification } from "./notifications";

export const updateAuthorInfo = createAction<AuthorInfo>(
  "saga/authorProfile/updateInfo"
);

function* handleUpdateAuthorInfo(action: PayloadAction<AuthorInfo>) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    yield call(authorProfileAPI.updateAuthorInfo, action.payload);
    yield put(
      pushNotification({
        text: "Successfully updated author info.",
        type: "success",
      })
    );
    yield put(fetchAuthorInfo());
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const updateFeaturedLink = createAction<FeaturedLink>(
  "saga/authorProfile/updateFeaturedLink"
);

function* handleUpdateFeaturedLink(action: PayloadAction<FeaturedLink>) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    yield call(authorProfileAPI.updateFeaturedLink, action.payload);
    yield put(fetchFeaturedLinks());
    yield put(
      pushNotification({
        text: "Successfully updated a featured link.",
        type: "success",
      })
    );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const deleteFeaturedLink = createAction<EntityId>(
  "saga/authorProfile/deleteFeaturedLink"
);
function* handleDeleteFeaturedLink(action: PayloadAction<EntityId>) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    yield call(authorProfileAPI.deleteFeaturedLink, action.payload);
    yield put(fetchFeaturedLinks());
    yield put(
      pushNotification({
        text: "Successfully deleted a featured link.",
        type: "success",
      })
    );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const createFeaturedLink = createAction<Omit<FeaturedLink, "id">>(
  "saga/authorProfile/createFeaturedLink"
);
function* handleCreateFeaturedLink(
  action: PayloadAction<Omit<FeaturedLink, "id">>
) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    yield call(authorProfileAPI.postFeaturedLink, action.payload);
    yield put(fetchFeaturedLinks());
    yield put(
      pushNotification({
        text: "Successfully created a featured link.",
        type: "success",
      })
    );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const fetchFeaturedLinks = createAction(
  "saga/authorProfile/fetchFeaturedLinks"
);
function* handleFetchFeaturedLinks(
  action: PayloadAction<Omit<FeaturedLink, "id">>
) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    const featuredLinks: FeaturedLink[] = yield call(
      authorProfileAPI.fetchFeaturedLinks
    );
    yield put(authorProfileActions.setFeaturedLinks(featuredLinks));
    // yield put(
    //   pushNotification({
    //     text: "Fetched featured links.",
    //     type: "success",
    //   })
    // );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const fetchAuthorInfo = createAction(
  "saga/authorProfile/fetchAuthorInfo"
);
function* handleFetchAuthorInfo(
  action: PayloadAction<Omit<FeaturedLink, "id">>
) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    const authorInfo: AuthorInfo = yield call(authorProfileAPI.fetchAuthorInfo);
    yield put(authorProfileActions.setAuthorInfo(authorInfo));
    // yield put(
    //   pushNotification({
    //     text: "Fetched featured links.",
    //     type: "success",
    //   })
    // );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const fetchTools = createAction("saga/authorProfile/fetchTools");
function* handleFetchTools(action: PayloadAction<Omit<FeaturedLink, "id">>) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    const tools: Tool[] = yield call(authorProfileAPI.fetchTools);
    yield put(authorProfileActions.setTools(tools));
    // yield put(
    //   pushNotification({
    //     text: "Fetched featured links.",
    //     type: "success",
    //   })
    // );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const fetchToolCategories = createAction(
  "saga/authorProfile/fetchToolCategories"
);
function* handleFetchToolCategories(
  action: PayloadAction<Omit<FeaturedLink, "id">>
) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    const toolCategories: ToolCategory[] = yield call(
      authorProfileAPI.fetchToolCategories
    );
    yield put(authorProfileActions.setToolCategories(toolCategories));
    // yield put(
    //   pushNotification({
    //     text: "Fetched featured links.",
    //     type: "success",
    //   })
    // );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const fetchToolCategoryRelations = createAction(
  "saga/authorProfile/fetchToolCategoryRelations"
);
function* handleFetchToolCategoryRelations(
  action: PayloadAction<Omit<FeaturedLink, "id">>
) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    const tcr: ToolCategoryRelation[] = yield call(
      authorProfileAPI.fetchToolCategoryrRelations
    );
    yield put(authorProfileActions.setToolCategoryRelations(tcr));
    // yield put(
    //   pushNotification({
    //     text: "Fetched featured links.",
    //     type: "success",
    //   })
    // );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export const fetchData = createAction("saga/authorProfile/fetchData");
function* handleFetchData(action: PayloadAction<Omit<FeaturedLink, "id">>) {
  try {
    yield put(authorProfileActions.setStatus("pending"));
    const featuredLinks: AuthorData = yield call(authorProfileAPI.fetchData);
    yield put(authorProfileActions.setData(featuredLinks));
    // yield put(
    //   pushNotification({
    //     text: "Fetched data.",
    //     type: "success",
    //   })
    // );
    yield put(authorProfileActions.setStatus("fulfilled"));
  } catch (error) {
    if ((yield select(selectIsAdminAuthenticated)) as boolean) {
      yield put(
        pushNotification({
          text: error.toString(),
          type: "error",
        })
      );
    }

    yield put(authorProfileActions.setStatus("rejected"));
  }
}

export function* processAuthorProfile() {
  yield takeEvery("saga/authorProfile/updateInfo", handleUpdateAuthorInfo);
  yield takeEvery(
    "saga/authorProfile/updateFeaturedLink",
    handleUpdateFeaturedLink
  );
  yield takeEvery(
    "saga/authorProfile/deleteFeaturedLink",
    handleDeleteFeaturedLink
  );
  yield takeEvery(
    "saga/authorProfile/createFeaturedLink",
    handleCreateFeaturedLink
  );
  yield takeEvery(
    "saga/authorProfile/fetchFeaturedLinks",
    handleFetchFeaturedLinks
  );
  yield takeEvery("saga/authorProfile/fetchTools", handleFetchTools);
  yield takeEvery(
    "saga/authorProfile/fetchToolCategories",
    handleFetchToolCategories
  );
  yield takeEvery(
    "saga/authorProfile/fetchToolCategoryRelations",
    handleFetchToolCategoryRelations
  );
  yield takeEvery("saga/authorProfile/fetchAuthorInfo", handleFetchAuthorInfo);
  yield takeEvery("saga/authorProfile/fetchData", handleFetchData);
  yield put(fetchData());
}
